import { getUniqueRecordsByProperty } from './CommonUtils';
import type {
  IPrintTypeRecord,
  IPrintTypeItem,
  IDecoratingItem,
  IDecorMethodRecord,
} from '@/common/interfaces/print-decor.interface';
import {
  APPLIQUE_PRINT_TYPE_ID,
  OLD_PRINT_TYPES,
  STREAMLINE_PRINT_TYPES,
  WOVEN_TAGS_VARIANT_ID,
} from '@/constants/PrintTypes';
import {
  DECORATING_LOCATIONS,
  DECORATING_METHODS,
  OLD_DECORATING_METHODS,
} from '@/constants/DecorMethods';
import type { IDesign } from '@/common/interfaces/design.interface';

/**
 * Method to form the output of the print types to be displayed.
 * (This method covers old and new print types.)
 *
 * @param printTypes - The print types to be displayed
 */
export const getPrintTypeList = (printTypes: IPrintTypeItem[] | string) => {
  if (
    (Array.isArray(printTypes) && printTypes.length > 0) ||
    (typeof printTypes === 'string' && printTypes.length > 0)
  ) {
    /** Old Print Types */
    if (typeof printTypes === 'string') {
      const selectedPrintTypeList = printTypes.split(';');
      let printTypeRecords: IPrintTypeRecord[] = [];

      selectedPrintTypeList.forEach((printType) => {
        const printTypeNumber = printType.split(':')[0];
        const printTypeRecord = OLD_PRINT_TYPES.find(
          (record: IPrintTypeRecord) => record.id === Number(printTypeNumber)
        );

        if (printTypeRecord) {
          printTypeRecords.push(printTypeRecord);
        }
      });

      printTypeRecords = printTypeRecords.filter((record) => record.imageSrc && record.type);

      return getUniqueRecordsByProperty(printTypeRecords, 'id', 'variant');
    }

    let updatedPrintTypes = [];

    /** Filtering the print locations that don't have all the data needed to get the quote.
     * These are mainly the print locations that are created from DT for personalized names and numbers.
     */
    const filteredPrintTypes = printTypes?.filter((printType: IPrintTypeItem) => {
      if (printType.printType.id === 1 || printType.printType.id === 9) {
        return !!printType.numberOfColors;
      }
      if (printType.printType.id === 12 || printType.printType.id === 13) {
        return true;
      }

      if (
        printType.printType.id === APPLIQUE_PRINT_TYPE_ID &&
        printType.variant?.id === WOVEN_TAGS_VARIANT_ID
      ) {
        return typeof printType.numberOfTags === 'number';
      }

      return !!printType.estimatedSize;
    });

    /** New Print Types */
    updatedPrintTypes = filteredPrintTypes?.map((printType: IPrintTypeItem) => {
      const selectedPrintType = STREAMLINE_PRINT_TYPES.find(
        (record) => record.id === printType.printType.id
      );
      const variant = selectedPrintType?.variants.find((v) => v.id === printType.variant.id);

      return {
        id: printType.printType.id,
        type: selectedPrintType ? selectedPrintType.type : '',
        imageSrc: variant ? variant.imageSrc : '',
        variant: variant ? variant.label : '',
        sImageSrc: variant ? variant.sImageSrc : '',
      };
    });

    updatedPrintTypes =
      updatedPrintTypes?.filter((printType) => printType.imageSrc && printType.type) || [];
    return getUniqueRecordsByProperty(updatedPrintTypes, 'id', 'variant');
  }

  return [];
};

/**
 * Method to return the decoration type list.
 * (This method covers old and new decoration methods.)
 *
 * @param decorMethods - List of decorators.
 */
export const getDecoratingTypeList = (decorMethods: IDecoratingItem[] | string) => {
  if (decorMethods) {
    if (typeof decorMethods === 'string') {
      const numbersArray = decorMethods.match(/(\d+)/g);
      const numbersOnly = numbersArray?.map(Number);
      const uniqueDecorIds = [...new Set(numbersOnly)];

      if (uniqueDecorIds && uniqueDecorIds.length > 0) {
        return uniqueDecorIds
          .map((decorMethod: number) => {
            const selectedDecor = OLD_DECORATING_METHODS.find(
              (record) => record.value === decorMethod
            );

            if (selectedDecor?.value) {
              return {
                id: selectedDecor.value || null,
                type: selectedDecor.type ?? '',
                location: '',
                imageSrc: selectedDecor.imageSrc ?? '',
                sImageSrc: selectedDecor.sImageSrc ?? '',
              };
            }

            return null;
          })
          .filter((item): item is IDecorMethodRecord => item !== null);
      }

      return [];
    }

    let updatedDecorMethods = [];

    updatedDecorMethods = decorMethods.map((decorMethod: IDecoratingItem) => {
      const selectedDecor = DECORATING_METHODS.find(
        (record) => record.id === decorMethod.decoratingMethod.id
      );

      let selectedDecorLocation = null;

      if (decorMethod.locationType) {
        selectedDecorLocation = DECORATING_LOCATIONS.find(
          (record) => record.id === decorMethod.locationType.id
        );
      }

      return {
        id: Number(decorMethod.decoratingMethod.id),
        type: selectedDecor ? selectedDecor.name : '',
        location: selectedDecorLocation ? selectedDecorLocation.name : '',
        imageSrc: selectedDecor ? selectedDecor.imageSrc : '',
        sImageSrc: selectedDecor ? selectedDecor.sImageSrc : '',
      };
    });

    updatedDecorMethods =
      updatedDecorMethods.filter((decorMethod) => decorMethod.imageSrc && decorMethod.type) || [];

    return getUniqueRecordsByProperty(updatedDecorMethods, 'id', 'location');
  }

  return [];
};

/**
 * Method to get the print and decoration list.
 *
 * @param design - The design object.
 */
export const getPrintDecorList = (design: IDesign) => {
  const { printInfo, printTypeInfo, decorMethodInfo, decoratingMethodInfo } = design;

  let designPrintTypes = printTypeInfo?.length
    ? getPrintTypeList(printTypeInfo as IPrintTypeItem[])
    : getPrintTypeList(printInfo ?? []);

  let designDecorMethods = decorMethodInfo?.length
    ? getDecoratingTypeList(decorMethodInfo as IDecoratingItem[])
    : getDecoratingTypeList(decoratingMethodInfo ?? []);

  designPrintTypes = designPrintTypes.filter(
    (designPrintType) => designPrintType?.imageSrc && designPrintType?.type
  );

  designDecorMethods = designDecorMethods.filter(
    (designDecorMethod) => designDecorMethod.imageSrc && designDecorMethod.type
  );

  return {
    designPrintTypes,
    designDecorMethods,
  };
};

/**
 * Creates a custom decorating item record.
 *
 * @param {number} id - The ID of the decorating method.
 * @param {string} name - The name of the decorating method.
 * @return {IDecoratingItem} The created decorating item.
 */
const createCustomDecorRecord = (id: number, name: string): IDecoratingItem => ({
  note: null,
  color: null,
  productType: {
    name: null,
  },
  locationType: {
    id: null,
    name: null,
  },
  stripeLocation: null,
  numOfDecorItems: null,
  numberOfStripes: null,
  decoratingMethod: {
    id,
    name,
  },
});

/**
 * Creates a default print info object with the given description.
 *
 * @param {string} description - The description of the print info object.
 * @return {IPrintInfo} - The default print info object.
 */
export const defaultPrintInfo = (description = '') => ({
  placement: '',
  printingType: 1,
  description,
  customNamesHex: null,
  customNumbersHex: null,
  hasCustomNames: false,
  hasCustomNumbers: false,
  printTypeInfo: {
    printType: {
      id: 1,
      name: 'Screen Printing',
    },
    variant: {
      id: 1,
      name: 'Standard',
    },
    estimatedSize: {
      id: null,
      name: '',
    },
    description,
    numberOfColors: 1,
    oversized: false,
  },
});

/**
 * Generates an array of decorating details by filtering out custom information
 * from the provided `decorMethodInfo` array. If `hasCustomNames` is true,
 * a custom decorating detail record for 'Custom Names' is added. If `hasCustomNumbers`
 * is true, a custom decorating detail record for 'Custom Numbers' is added.
 *
 * @param {IDecoratingItem[]} decorMethodInfo - An array of decorating items.
 * @param {boolean} hasCustomNames - Indicates whether custom names are present.
 * @param {boolean} hasCustomNumbers - Indicates whether custom numbers are present.
 * @return {IDecoratingItem[]} An array of decorating details without custom information.
 */
export const getDecorDetails = (
  decorMethodInfo: IDecoratingItem[] | null,
  hasCustomNames: boolean,
  hasCustomNumbers: boolean
) => {
  const isCustomInfoExists = hasCustomNames || hasCustomNumbers;

  if (!decorMethodInfo) {
    const DECOR_LIST = [];

    if (hasCustomNames) {
      DECOR_LIST.push(createCustomDecorRecord(8, 'Custom Names'));
    }

    if (hasCustomNumbers) {
      DECOR_LIST.push(createCustomDecorRecord(9, 'Custom Numbers'));
    }

    return DECOR_LIST.length ? DECOR_LIST : null;
  }

  if (isCustomInfoExists) {
    const decorDetailsWithOutCustomInfo = decorMethodInfo.filter(
      (decorDetail: IDecoratingItem) => ![8, 9].includes(decorDetail.decoratingMethod.id)
    );

    if (hasCustomNames) {
      decorDetailsWithOutCustomInfo.push(createCustomDecorRecord(8, 'Custom Names'));
    }

    if (hasCustomNumbers) {
      decorDetailsWithOutCustomInfo.push(createCustomDecorRecord(9, 'Custom Numbers'));
    }

    return decorDetailsWithOutCustomInfo.length ? decorDetailsWithOutCustomInfo : null;
  }

  return decorMethodInfo;
};

const CUSTOM_NAMES_DECOR_ID = 8;
const CUSTOM_NUMBERS_DECOR_ID = 9;

/** Custom Names / Numbers decor records for cart and checkout display. */
export const getCustomNamesAndNumbersDecorList = (
  decorMethodInfo: IDecoratingItem[] | null,
  hasCustomNames: boolean,
  hasCustomNumbers: boolean
): IDecorMethodRecord[] => {
  const decorDetails = getDecorDetails(decorMethodInfo, hasCustomNames, hasCustomNumbers);

  if (!decorDetails) {
    return [];
  }

  return getDecoratingTypeList(decorDetails).filter(
    (method) => method.id === CUSTOM_NAMES_DECOR_ID || method.id === CUSTOM_NUMBERS_DECOR_ID
  );
};
